import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { Button, AgGridTable, Pagination } from 'components/common';
import PropTypes from 'prop-types';
import { FiDownload, FiSearch, FiX } from 'react-icons/fi';
import apiAxios from 'helpers/apiAxios';
import { downloadBlob } from 'helpers/exportCsv';
import DETAIL_COLUMNS from './detailColumns';

const fmt = (n) => Number(n || 0).toLocaleString('en-US');
const BASE = '/crm/dashboard/crm';

// Reusable server-side detail grid (pagination + column sort + global search +
// Excel export). Used inline at the bottom of each tab and inside the drawer.
const OverviewDetailGrid = ({ section, baseParams, extraParams, title, height }) => {
  const columnDefs = DETAIL_COLUMNS[section] || DETAIL_COLUMNS.sales;

  const [rows, setRows]                 = useState([]);
  const [loading, setLoading]           = useState(false);
  const [page, setPage]                 = useState(1);
  const [perPage, setPerPage]           = useState(50);
  const [totalPages, setTotalPages]     = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);
  const [sortBy, setSortBy]             = useState(null);
  const [sortDir, setSortDir]           = useState('DESC');
  const [search, setSearch]             = useState('');
  const [debounced, setDebounced]       = useState('');
  const [exporting, setExporting]       = useState(false);

  useEffect(() => {
    const t = setTimeout(() => setDebounced(search.trim()), 400);
    return () => clearTimeout(t);
  }, [search]);

  // Common params (without pagination) — drives reset + export.
  const queryParams = useMemo(() => {
    const p = { ...baseParams, ...extraParams, section };
    if (debounced) p.q = debounced;
    if (sortBy) { p.sort_by = sortBy; p.sort_dir = sortDir; }
    return p;
  }, [baseParams, extraParams, section, debounced, sortBy, sortDir]);

  const queryKey = useMemo(() => JSON.stringify(queryParams), [queryParams]);

  // Reset to page 1 whenever the query (filters/search/sort/section) changes.
  useEffect(() => { setPage(1); }, [queryKey]);

  const fetchRows = useCallback(async () => {
    setLoading(true);
    try {
      const res = await apiAxios.get(`${BASE}/get_detailed_data`, {
        params: { ...queryParams, pagination: { page, per_page: perPage } }
      });
      setRows(res.data?.data || []);
      setTotalPages(res.data?.pagination?.total_pages || 1);
      setTotalRecords(res.data?.pagination?.total_records || 0);
    } catch {
      setRows([]);
      setTotalPages(1);
      setTotalRecords(0);
    } finally {
      setLoading(false);
    }
  }, [queryParams, page, perPage]);

  useEffect(() => { fetchRows(); }, [fetchRows]);

  const handleSort = ({ colId, sort }) => {
    if (!sort) { setSortBy(null); return; }
    setSortBy(colId);
    setSortDir(sort === 'asc' ? 'ASC' : 'DESC');
  };

  const handleExport = async () => {
    setExporting(true);
    try {
      const res = await apiAxios.get(`${BASE}/export_detailed_data`, {
        params: queryParams,
        responseType: 'blob'
      });
      downloadBlob(res.data, `crm-${section}-${new Date().toISOString().slice(0, 10)}.xlsx`);
    } catch {
      alert('Export failed. Please try again.');
    } finally {
      setExporting(false);
    }
  };

  return (
    <div className="tw-bg-white tw-rounded-2xl tw-border tw-border-gray-200 tw-shadow-sm tw-overflow-hidden">
      <div className="tw-flex tw-items-center tw-justify-between tw-gap-3 tw-px-4 tw-py-3 tw-border-b tw-border-gray-100 tw-flex-wrap">
        <div className="tw-flex tw-items-center tw-gap-2">
          <span className="tw-font-bold tw-text-gray-800">{title || 'Details'}</span>
          {!loading && (
            <span className="tw-text-xs tw-text-gray-500 tw-bg-gray-100 tw-px-2 tw-py-0.5 tw-rounded-xl">{fmt(totalRecords)} records</span>
          )}
        </div>
        <div className="tw-flex tw-items-center tw-gap-2">
          <div className="tw-relative">
            <FiSearch className="tw-absolute tw-left-2.5 tw-top-2.5 tw-text-gray-400" size={13} />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search project / quote / job…"
              className="tw-w-56 tw-pl-8 tw-pr-7 tw-py-1.5 tw-text-xs tw-border tw-border-gray-200 tw-rounded focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-blue-500"
            />
            {search && (
              <button type="button" onClick={() => setSearch('')} className="tw-absolute tw-right-2 tw-top-2 tw-text-gray-400 hover:tw-text-gray-600 tw-bg-transparent tw-border-0 tw-p-0 tw-cursor-pointer">
                <FiX size={12} />
              </button>
            )}
          </div>
          <Button
            variant="secondary"
            size="sm"
            leftIcon={FiDownload}
            loading={exporting}
            disabled={totalRecords === 0}
            onClick={handleExport}
            className="tw-border-emerald-300 tw-bg-emerald-50 tw-text-emerald-700 hover:tw-bg-emerald-100"
            title="Export to Excel (.xlsx)"
          >
            {exporting ? 'Exporting…' : 'Excel'}
          </Button>
        </div>
      </div>

      <AgGridTable
        rowData={rows}
        columnDefs={columnDefs}
        loading={loading}
        pagination={false}
        filter={false}
        sort
        rowHeight={36}
        headerHeight={40}
        fontSize="12px"
        height={height || '460px'}
        enableCellTextSelection
        onSortChanged={handleSort}
      />

      {totalPages > 1 && (
        <div className="tw-px-3 tw-py-2 tw-border-t tw-border-gray-100">
          <Pagination
            currentPage={page}
            totalPages={totalPages}
            totalResults={totalRecords}
            perPage={perPage}
            onPageChange={setPage}
            onPageSizeChange={(s) => { setPerPage(s); setPage(1); }}
            disabled={loading}
          />
        </div>
      )}
    </div>
  );
};

OverviewDetailGrid.propTypes = {
  section:     PropTypes.string.isRequired,
  baseParams:  PropTypes.object.isRequired,
  extraParams: PropTypes.object,
  title:       PropTypes.string,
  height:      PropTypes.string
};
OverviewDetailGrid.defaultProps = { extraParams: {}, title: 'Details', height: '460px' };

export default OverviewDetailGrid;
